import { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import { Volume2, VolumeX, Maximize, Play, Pause, X, Airplay } from 'lucide-react'

interface VideoPlayerProps {
  src: string
  channelName: string
  channelIcon?: string
  onClose?: () => void
  index: number
}

export default function VideoPlayer({ src, channelName, channelIcon, onClose, index }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(index > 0) // Mute all except first
  const [showControls, setShowControls] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [airplayAvailable, setAirplayAvailable] = useState(false)
  const hideControlsTimeout = useRef<number | null>(null)

  // Check for AirPlay availability (Safari/WebKit)
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if ('webkitShowPlaybackTargetPicker' in video) {
      setAirplayAvailable(true)

      const handleAvailability = (event: Event) => {
        const e = event as CustomEvent
        setAirplayAvailable(e.detail?.available !== false)
      }

      video.addEventListener('webkitplaybacktargetavailabilitychanged', handleAvailability)
      return () => {
        video.removeEventListener('webkitplaybacktargetavailabilitychanged', handleAvailability)
      }
    }
  }, [])

  useEffect(() => {
    if (!videoRef.current || !src) return

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 30,
        maxBufferLength: 10,
        maxMaxBufferLength: 30,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
      })

      hls.loadSource(src)
      hls.attachMedia(videoRef.current)

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Begin on the highest-quality rendition instead of letting ABR ramp up
        // from a low variant. Setting startLevel (not currentLevel) keeps
        // adaptation enabled, so playback can still step down if the connection
        // genuinely can't sustain the top variant — best quality we can get.
        if (hls.levels.length > 1) {
          hls.startLevel = hls.levels.length - 1
        }
        videoRef.current?.play().catch(() => {})
      })

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.error('[VideoPlayer] HLS Error:', {
          type: data.type,
          details: data.details,
          fatal: data.fatal,
          reason: data.reason,
          url: data.url,
        })

        if (data.fatal) {
          let errorMsg = `Stream error: ${data.type}`
          if (data.details === 'bufferAppendError' || data.details === 'bufferAppendingError') {
            errorMsg = 'Codec not supported (may be HEVC/H.265)'
          } else if (data.details === 'fragParsingError') {
            errorMsg = 'Stream format error'
          } else if (data.details) {
            errorMsg = `${data.type}: ${data.details}`
          }

          setError(errorMsg)

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad()
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError()
          }
        }
      })

      hlsRef.current = hls

      return () => {
        hls.destroy()
        hlsRef.current = null
      }
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = src
      videoRef.current.play().catch(() => {})
    }
  }, [src])

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        containerRef.current.requestFullscreen()
      }
    }
  }

  const showAirplayPicker = () => {
    const video = videoRef.current as HTMLVideoElement & {
      webkitShowPlaybackTargetPicker?: () => void
    }
    if (video?.webkitShowPlaybackTargetPicker) {
      video.webkitShowPlaybackTargetPicker()
    }
  }

  const handleMouseMove = () => {
    setShowControls(true)
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current)
    }
    hideControlsTimeout.current = window.setTimeout(() => {
      setShowControls(false)
    }, 3000)
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full group"
      style={{ background: 'var(--gs-bg-0, #000)' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        muted={isMuted}
        playsInline
        autoPlay
        {...{ 'x-webkit-airplay': 'allow' } as any}
      />

      {error && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(15% 0.012 250 / 0.9)' }}>
          <div className="text-center">
            <p className="text-sm mb-3" style={{ color: 'oklch(82% 0.18 25)' }}>{error}</p>
            <button
              onClick={() => {
                setError(null)
                hlsRef.current?.startLoad()
              }}
              className="px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer transition-all"
              style={{
                background: 'var(--gs-ink-0, #fff)',
                color: 'var(--gs-bg-0, #000)',
                border: 'none',
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Audio indicator — always visible when unmuted */}
      {!isMuted && (
        <div
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md gs-mono text-[9px] font-bold"
          style={{
            background: 'var(--gs-accent-soft)',
            border: '1px solid var(--gs-accent)',
            color: 'var(--gs-accent)',
            letterSpacing: '0.08em',
          }}
        >
          <Volume2 className="w-3 h-3" />
          AUDIO
        </div>
      )}

      {/* Channel info overlay — top gradient */}
      <div
        className={`absolute top-0 left-0 right-0 p-3 transition-opacity duration-200 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ background: 'linear-gradient(180deg, oklch(15% 0.012 250 / 0.85), transparent)' }}
      >
        <div className="flex items-center gap-2.5">
          {channelIcon ? (
            <img
              src={channelIcon}
              alt=""
              className="w-7 h-7 rounded-lg object-cover flex-shrink-0"
              style={{ border: '1px solid var(--gs-glass-stroke)' }}
            />
          ) : (
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 gs-mono text-[10px] font-bold"
              style={{
                background: 'oklch(28% 0.10 250)',
                color: 'oklch(85% 0.12 250)',
                border: '1px solid var(--gs-glass-stroke)',
              }}
            >
              {channelName.substring(0, 2).toUpperCase()}
            </div>
          )}
          <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--gs-ink-0, #fff)' }}>
            {channelName}
          </span>
        </div>
      </div>

      {/* Controls overlay — bottom gradient */}
      <div
        className={`absolute bottom-0 left-0 right-0 p-3 transition-opacity duration-200 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ background: 'linear-gradient(180deg, transparent, oklch(15% 0.012 250 / 0.85))' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={togglePlay}
              className="p-2 rounded-full transition-colors cursor-pointer"
              style={{ color: 'var(--gs-ink-0, #fff)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button
              onClick={toggleMute}
              className="p-2 rounded-full transition-colors cursor-pointer"
              style={{ color: 'var(--gs-ink-0, #fff)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex items-center gap-1">
            {airplayAvailable && (
              <button
                onClick={showAirplayPicker}
                className="p-2 rounded-full transition-colors cursor-pointer"
                style={{ color: 'var(--gs-ink-0, #fff)' }}
                title="AirPlay"
                onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.15)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <Airplay className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-full transition-colors cursor-pointer"
              style={{ color: 'var(--gs-ink-0, #fff)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Maximize className="w-4 h-4" />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-full transition-colors cursor-pointer"
                style={{ color: 'var(--gs-ink-0, #fff)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.15)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
